import {perfLogger} from '@/buried/performance';
import axios, {AxiosResponse} from 'axios';
import cacheWrapper from 'axios-pre-request';
import assert from 'assert';
import {WizNetworkError, WizInternalError, WizKnownError, WizNoPermissionError} from './error';
import {WizRequestOptions} from '../../interface';
import {IAddFrontTraceInfo} from './addFrontTraceInfo';

axios.defaults.timeout = 15 * 1000;
const axiosInstance = cacheWrapper(axios);

interface Headers {[index: string]: string}

function getContentLengthFromHeaders(headers: Headers) {
    for (const key of Object.keys(headers)) {
        const lowerCaseKey = key.toLowerCase();
        if (lowerCaseKey === 'content-length') {
            return Number.parseInt(headers[key], 10);
        }
    }

    return -1;
}

function getErrorFromHeaders(headers: Headers, options: WizRequestOptions, response?: AxiosResponse<any>) {
    let code = -1;
    let externCode = '';
    for (const key of Object.keys(headers)) {
        const lowerCaseKey = key.toLowerCase();
        if (lowerCaseKey === 'x-wiz-code') {
            code = Number.parseInt(headers[key], 10);
        } else if (lowerCaseKey === 'x-wiz-externcode') {
            externCode = headers[key];
        }
    }
    //
    if (code) {
        if (code === 2000) {
            console.error(`invalid param: ${options.url}`);
        }
        let errorMsg = '';
        if (response) {
            const {status, data, headers} = response;
            errorMsg = `network status ${status}, response ${data}, server ${headers.server}`;
        }
        return new WizKnownError(
            `server error from api ${options.url} ${errorMsg || ''}`,
            code,
            options,
            externCode
        );
    }
    return null;
}


function checkResultData(result: AxiosResponse<any>, options?: any) {
    let byteLength: number | null = null;
    if (typeof navigator !== 'undefined' && navigator.product === 'ReactNative') {
        if (result.data.constructor === ArrayBuffer) {
            byteLength = result.data.byteLength;
        }
    }
    const headerContentLength = getContentLengthFromHeaders(result.headers);
    const dataLength = byteLength || result.data.length;
    if (headerContentLength !== -1) {
        if (dataLength !== headerContentLength) {
            // eslint-disable-next-line max-len
            throw new WizNetworkError(`Failed to download data, invalid content length: ${result.data.length}, ${headerContentLength}`, options);
        }
    }
}

export function weirwoodApiLog({
    url,
    method,
    params,
    status,
    response,
}: {
    url: string;
    method: string;
    params: any;
    status: string;
    response: any;
}) {
    try {
        const __Weirwood = (window as any).__Weirwood;
        if (__Weirwood && __Weirwood.error && __Weirwood.error.captureAPIException) {
            const data = {
                url, // 必填，错误接口路径
                method, // 必填，请求 method
                params, // 可选，请求参数，如果是 JSON 对象，不需要 JSON 序列化，直接赋值给 params 即可
                status, // 必填，响应状态，可以自定义
                response, // 可选，响应结果，如果是 JSON 对象，不需要 JSON 序列化，直接赋值给 response 即可
            };
            __Weirwood.error.captureAPIException(data);
        }
    } catch (error) {
        console.error('weirwoodApiLog error: ', error);
    }
}

// eslint-disable-next-line complexity
function operateAxiosResult(options: WizRequestOptions, result: AxiosResponse<any>) {
    if (result.status !== 200 && result.status !== 201) {
        const error = getErrorFromHeaders(result.headers, options, result);
        if (error) {
            throw error;
        }
        throw new WizNetworkError(result.statusText, options);
    }
    //
    if (!result.data) {
        throw new WizInternalError('no data returned', options);
    }
    //
    const data = result.data;
    if (options.responseType === 'arraybuffer') {
        checkResultData(result, options);
    } else if (data.code === 40001 && data.redirectUrl) {
        window.location.href = data.redirectUrl;
        throw new Error('');
    } else if (data.returnCode === 403) {
        const message = JSON.stringify(data.result || data.returnMessage || data.message || '');
        throw new WizNoPermissionError(message, options, data);
    } else if ((data.returnCode || data.code) !== 200) {
        weirwoodApiLog({
            url: options.url || '',
            method: options.method || '',
            params: options.params,
            status: data.returnCode || data.code,
            response: data,
        });
        // 是否返回所有错误信息
        if (options.returnFullError) {
            throw new WizKnownError(
                data.returnMessage || data.message || '',
                data.returnCode || data.code,
                options,
                data.externCode,
                data
            );
        } else {
            throw new WizKnownError(
                data.returnMessage || data.message || '',
                data.returnCode || data.code,
                options,
                data.externCode
            );
        }
    }

    if (options.returnFullResult) {
        return data;
    }

    return data.result || data.data;
}

const serverEnv = process.env.REACT_APP_SERVER_ENV || '';

function fixHeader(opt: WizRequestOptions) {
    const options = opt;
    assert(options, 'no options');
    //
    const wikiToken = options.wikiToken;
    const xWikiRteSToken = options.xWikiRteSToken;
    const appId = options.appId;
    if (wikiToken && !options.headers) {
        options.headers = {
            'x-wiki-token': wikiToken,
        };
    } else if (wikiToken) {
        options.headers['x-wiki-token'] = wikiToken;
    }
    const wikiAccessToken = options.wikiAccessToken;
    if (wikiAccessToken && !options.headers) {
        options.headers = {
            'x-wiki-access': wikiAccessToken,
        };
    } else if (wikiAccessToken) {
        options.headers['x-wiki-access'] = wikiAccessToken;
    }
    if (xWikiRteSToken) {
        options.headers['x-wiki-rte-stoken'] = xWikiRteSToken;
    }
    if (appId) {
        !options.headers && (options.headers = {});
        options.headers.appId = appId;
    }
    if (serverEnv) {
        options.headers = {
            ...(options.headers || {}),
            'x-wiz-trace': serverEnv,
        };
    }
    return options;
}

// eslint-disable-next-line complexity
export async function standardRequest(
    opt: WizRequestOptions,
    addFrontTraceInfo: ((opt: WizRequestOptions) => WizRequestOptions) | null
) {
    const options = (opt.noFrontTrace || !addFrontTraceInfo)
        ? fixHeader(opt)
        : addFrontTraceInfo(fixHeader(opt));
    options.withCredentials = true;
    assert(options, 'no options');
    //
    try {
        const result = await axiosInstance(options);
        if (result.data.returnCode !== 200 && result.data.code !== 200) {
            perfLogger.sourceAndApiError.logApiError(
                options.url || '',
                result.data.returnCode,
                result.data.returnMessage
            );
        }
        return operateAxiosResult(options, result);
    } catch (err) {
        // 如果是网络错误上报
        if (!(err instanceof WizKnownError)) {
            perfLogger.sourceAndApiError.logApiError(options.url || '', 0, err);
        }
        if (err.response) {
            const headers = err.response.headers;
            const error = getErrorFromHeaders(headers, options, err.response);
            if (error) {
                throw error;
            }
        } else if (err.code === 'ENOTFOUND') {
            const message = `Failed to connect ${err.hostname}, err.code: 'ENOTFOUND'`;
            throw new WizNetworkError(message, options);
        } else if (err instanceof WizKnownError) {
            throw err;
        }
        throw new WizNetworkError(err.message, options);
    }
}


export async function downloadToData(
    opt: WizRequestOptions,
    addFrontTraceInfo: IAddFrontTraceInfo | null
) {
    const options = addFrontTraceInfo ? addFrontTraceInfo(fixHeader(opt)) : fixHeader(opt);
    options.responseType = 'blob';
    options.withCredentials = true;
    assert(options, 'no options');
    const response = await axios(options);
    if (response.status !== 200) {
        const {status, statusText, data} = response;
        throw new WizNetworkError(`network status ${status}, status text${statusText}, response ${data}`, options);
    }
    //
    if (!response.data) {
        throw new WizInternalError('no data returned', options);
    }
    const {
        data,
        headers,
    } = response;
    return {
        data,
        headers,
    };
}
